import React, { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search, LayoutList, LayoutGrid, GripVertical,
  Eye, ShieldCheck, Loader2
} from "lucide-react";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified CRM Pipeline View
 * Shared by Borrowers, Lenders, and Investors with drag-and-drop Kanban.
 *
 * Props:
 * - title: string
 * - subtitle: string
 * - stages: [{ id, label, color, border }]
 * - items: [{ user_id, full_name, email, pipeline_stage, ...extraFields }]
 * - deriveStageFn: (item) => stageId
 * - renderCardContent: (item) => JSX  (extra content below name/email)
 * - tableColumns: [{ key, label, render? }]
 * - onSelectUser: (userId) => void
 * - onStageChange: (userId, newStage) => void
 * - entityType: "borrower" | "lender" | "investor"
 * - extraActions: (item) => JSX  (e.g. accreditation approve button)
 * - testIdPrefix: string (for data-testid)
 */
export default function CRMPipelineView({
  title, subtitle, stages, items, deriveStageFn,
  renderCardContent, tableColumns, onSelectUser,
  onStageChange, entityType, extraActions, testIdPrefix = "crm-p"
}) {
  const [viewMode, setViewMode] = useState("pipeline");
  const [search, setSearch] = useState("");
  const [dragItem, setDragItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [updatingStage, setUpdatingStage] = useState(null);
  const dragRef = useRef(null);
  const token = localStorage.getItem("ic_token");

  const filtered = items.filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (item.full_name || "").toLowerCase().includes(q) ||
      (item.email || "").toLowerCase().includes(q) ||
      (item.entity_name || "").toLowerCase().includes(q) ||
      (item.company || "").toLowerCase().includes(q);
  });

  const stageCounts = {};
  stages.forEach(s => { stageCounts[s.id] = 0; });
  items.forEach(item => {
    const st = deriveStageFn(item);
    stageCounts[st] = (stageCounts[st] || 0) + 1;
  });

  // ── Drag & Drop Handlers ──
  const handleDragStart = useCallback((e, item) => {
    setDragItem(item);
    dragRef.current = item;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.user_id);
    // Add ghost styling
    e.target.style.opacity = "0.5";
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = "1";
    setDragItem(null);
    setDragOverStage(null);
    dragRef.current = null;
  }, []);

  const handleDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverStage(null);
  }, []);

  const handleDrop = useCallback(async (e, targetStageId) => {
    e.preventDefault();
    setDragOverStage(null);
    const item = dragRef.current;
    if (!item) return;

    const currentStage = deriveStageFn(item);
    if (currentStage === targetStageId) return;

    setUpdatingStage(item.user_id);
    try {
      await axios.put(`${API}/admin/users/${item.user_id}/pipeline-stage`, {
        stage: targetStageId
      }, { headers: { Authorization: `Bearer ${token}` } });
      onStageChange?.(item.user_id, targetStageId);
    } catch (err) {
      console.error("Stage update failed:", err);
    } finally {
      setUpdatingStage(null);
    }
  }, [deriveStageFn, onStageChange, token]);

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-container`}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">{title}</h2>
          <p className="text-xs text-[#888] mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-[#f5f0e8] border border-[#e8e0d0]" data-testid={`${testIdPrefix}-view-toggle`}>
          <button onClick={() => setViewMode("pipeline")} data-testid={`${testIdPrefix}-view-pipeline`}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "pipeline" ? "bg-white text-emerald-700 shadow-sm" : "text-[#888]"}`}>
            <LayoutGrid className="h-3.5 w-3.5" /> Pipeline
          </button>
          <button onClick={() => setViewMode("table")} data-testid={`${testIdPrefix}-view-table`}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === "table" ? "bg-white text-emerald-700 shadow-sm" : "text-[#888]"}`}>
            <LayoutList className="h-3.5 w-3.5" /> Table
          </button>
        </div>
      </div>

      {/* Stage KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stages.map((stage, i) => (
          <motion.div key={stage.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className={`rounded-xl border ${stage.border} bg-white p-3`} data-testid={`${testIdPrefix}-stage-${stage.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${stage.color}`} />
              <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{stage.label}</span>
            </div>
            <div className="text-xl font-bold text-[#0A0A0A]">{stageCounts[stage.id]}</div>
          </motion.div>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#aaa]" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}...`}
            data-testid={`${testIdPrefix}-search`}
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-[#e8e0d0] bg-white text-sm focus:outline-none focus:border-emerald-400" />
        </div>
        <span className="text-xs text-[#999]">{filtered.length} of {items.length}</span>
      </div>

      {/* ── PIPELINE VIEW (Kanban with Drag & Drop) ── */}
      {viewMode === "pipeline" && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid={`${testIdPrefix}-pipeline`}>
          {stages.map(stage => {
            const stageItems = filtered.filter(item => deriveStageFn(item) === stage.id);
            const isDropTarget = dragOverStage === stage.id;

            return (
              <div key={stage.id} className="min-w-[240px] w-[240px] flex-shrink-0"
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}>
                <div className={`rounded-t-xl px-3 py-2 ${stage.color} text-white text-[10px] font-bold uppercase tracking-wider flex items-center justify-between`}>
                  <span>{stage.label}</span>
                  <span className="bg-white/30 px-1.5 py-0.5 rounded text-[9px]">{stageItems.length}</span>
                </div>
                <div className={`border border-t-0 rounded-b-xl p-2 space-y-2 min-h-[200px] max-h-[500px] overflow-y-auto transition-all duration-200 ${
                  isDropTarget ? "bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200" : "bg-[#faf7f2] border-[#e8e0d0]"
                }`}>
                  <AnimatePresence>
                    {stageItems.map(item => (
                      <motion.div key={item.user_id}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: updatingStage === item.user_id ? 0.5 : 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragEnd={handleDragEnd}
                        className={`rounded-xl border bg-white p-3 cursor-grab active:cursor-grabbing hover:border-emerald-300 hover:shadow-sm transition-all group ${
                          dragItem?.user_id === item.user_id ? "border-emerald-400 shadow-md" : "border-[#e8e0d0]"
                        }`}
                        onClick={() => onSelectUser?.(item.user_id)}
                        data-testid={`${testIdPrefix}-card-${item.user_id}`}>

                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#0A0A0A] truncate">{item.full_name || "Unknown"}</div>
                            <div className="text-[10px] text-[#888] truncate">{item.email}</div>
                          </div>
                          <GripVertical className="h-4 w-4 text-[#ccc] group-hover:text-[#999] shrink-0 mt-0.5" />
                        </div>

                        {/* Per-entity extra content */}
                        {renderCardContent?.(item)}

                        {/* Extra actions (e.g. approve accreditation) */}
                        {extraActions?.(item)}

                        {updatingStage === item.user_id && (
                          <div className="flex items-center gap-1 mt-1 text-[9px] text-emerald-600">
                            <Loader2 className="h-3 w-3 animate-spin" /> Moving...
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {stageItems.length === 0 && !isDropTarget && (
                    <div className="text-[10px] text-[#bbb] text-center py-8">Empty</div>
                  )}
                  {isDropTarget && stageItems.length === 0 && (
                    <div className="text-[10px] text-emerald-500 text-center py-8 border-2 border-dashed border-emerald-300 rounded-lg">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── TABLE VIEW ── */}
      {viewMode === "table" && (
        <div className="rounded-xl border border-[#e8e0d0] overflow-hidden" data-testid={`${testIdPrefix}-table`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#faf7f2]">
                  {(tableColumns || []).map(col => (
                    <th key={col.key} className="text-left px-4 py-3 text-[9px] uppercase tracking-wider text-[#999] font-semibold border-b border-[#e8e0d0]">
                      {col.label}
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 text-[9px] uppercase tracking-wider text-[#999] font-semibold border-b border-[#e8e0d0]">
                    Stage
                  </th>
                  <th className="px-4 py-3 border-b border-[#e8e0d0] w-10" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.user_id} onClick={() => onSelectUser?.(item.user_id)}
                    className="hover:bg-[#faf7f2] cursor-pointer border-b border-[#f0ebe0] last:border-0 transition-colors"
                    data-testid={`${testIdPrefix}-row-${item.user_id}`}>
                    {(tableColumns || []).map(col => (
                      <td key={col.key} className="px-4 py-3">
                        {col.render ? col.render(item) : (
                          <span className="text-xs text-[#333]">{item[col.key] ?? "—"}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      {(() => {
                        const st = stages.find(s => s.id === deriveStageFn(item));
                        return st ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${st.border} bg-white`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${st.color}`} />
                            {st.label}
                          </span>
                        ) : <span className="text-xs text-[#999]">—</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={e => { e.stopPropagation(); onSelectUser?.(item.user_id); }}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700 transition-colors opacity-60 hover:opacity-100"
                        data-testid={`${testIdPrefix}-view-${item.user_id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={(tableColumns || []).length + 2} className="text-center py-12 text-[#999] text-xs">
                    No {title.toLowerCase()} found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
